/* MolCellQuest 問題データ — 転写・RNA・翻訳
   ★ 配列に追記すればそのまま反映されます（id は全体で一意に）。
   ★ diff : 80〜320。基礎 0-179 / 標準 180-269 / 応用 270+ */

const CAT = { track: "genome", category: "expression", categoryName: "転写・RNA・翻訳" };

export const GENOME_EXPRESSION = [
  {
    id: "exp01", ...CAT,
    title: "セントラルドグマの流れ", diff: 85, tags: ["セントラルドグマ", "転写", "翻訳"],
    explain: `<p>遺伝情報の流れについて、<b>知られていない</b>ものはどれか。</p>`,
    type: "radio",
    options: ["DNA → RNA", "RNA → タンパク質", "RNA → DNA", "タンパク質 → RNA"],
    answer: 3,
    hints: [
      "セントラルドグマは「情報はタンパク質から核酸へは戻らない」と述べています。",
      "逆転写（RNA→DNA）は実在しますが、タンパク質を鋳型に核酸を作る仕組みは見つかっていません。",
    ],
    solution: `<p><b>【ステップ1】各流れを確認する</b></p>
               <ul>
                 <li><b>DNA → RNA</b>：転写。RNAポリメラーゼが行う。</li>
                 <li><b>RNA → タンパク質</b>：翻訳。リボソームが行う。</li>
                 <li><b>DNA → DNA</b>：複製。DNAポリメラーゼが行う。</li>
                 <li><b>RNA → DNA</b>：逆転写。レトロウイルスやテロメラーゼが行う（例外的だが実在する）。</li>
                 <li><b>タンパク質 → RNA / DNA</b>：<b>知られていない</b>。</li>
               </ul>
               <p><b>【ステップ2】セントラルドグマの本質</b></p>
               <p>クリックの主張の核心は「配列情報はいったんタンパク質になったら、
               二度と核酸には戻せない」という<b>一方向性</b>です。
               アミノ酸配列から塩基配列を復元する装置は存在しません。</p>
               <p>したがって、正解は <b>タンパク質 → RNA (D)</b> です。</p>`,
  },

  {
    id: "exp02", ...CAT,
    title: "コドンとアミノ酸の数", diff: 110, tags: ["遺伝暗号", "コドン", "縮重"],
    explain: `<p>遺伝暗号は3塩基で1つのアミノ酸を指定する。</p>
              <p>4種類の塩基から作れるコドンの総数と、そのうち終止コドンの数の組み合わせとして正しいものはどれか。</p>`,
    type: "radio",
    options: ["16 と 1", "64 と 3", "64 と 1", "20 と 3"],
    answer: 1,
    hints: ["4種類の塩基を3つ並べる組み合わせの数を計算しましょう。", "終止コドンは UAA・UAG・UGA の3つです。"],
    solution: `<p><b>【ステップ1】コドンの総数</b></p>
               <p>各位置に4通りの塩基が入るので、4 × 4 × 4 = <b>64 通り</b>です。</p>
               <p><b>【ステップ2】終止コドン</b></p>
               <p><b>UAA・UAG・UGA</b> の <b>3つ</b>。残る 61 個が20種類のアミノ酸を指定します。</p>
               <p><b>【ステップ3】なぜ 61 対 20 なのか</b></p>
               <p>61 個で20種類を指定するので、多くのアミノ酸が複数のコドンを持ちます。
               これを<b>縮重（degeneracy）</b>といいます。縮重はコドンの<b>3文字目</b>に集中しており、
               3文字目の1塩基置換は<b>同義置換</b>になりやすい——つまり遺伝暗号は
               変異の影響を和らげるように配置されています。</p>
               <p>したがって、正解は <b>64 と 3 (B)</b> です。</p>`,
  },

  {
    id: "exp03", ...CAT,
    title: "mRNA前駆体のプロセシング", diff: 145, tags: ["キャップ", "ポリA", "スプライシング"],
    explain: `<p>真核生物のmRNA前駆体に加えられる修飾として、<b>当てはまらない</b>ものはどれか。</p>`,
    type: "radio",
    options: [
      "5' 末端への 7-メチルグアノシンキャップの付加",
      "3' 末端へのポリA尾部の付加",
      "イントロンの除去（スプライシング）",
      "5' 末端へのシャイン・ダルガノ配列の付加",
    ],
    answer: 3,
    hints: [
      "シャイン・ダルガノ配列は、どの生物のリボソームが使う目印でしょうか。",
      "真核生物のリボソームはキャップを目印にmRNAを探します（キャップ依存的翻訳開始）。",
    ],
    solution: `<p><b>【ステップ1】真核生物のmRNAプロセシング3点セット</b></p>
               <ul>
                 <li><b>5'キャップ</b>：7-メチルグアノシンが 5'-5' 三リン酸結合で付く。分解からの保護、核外輸送、翻訳開始の目印。</li>
                 <li><b>ポリA尾部</b>：3'末端に約200個のアデニン。安定性と翻訳効率を高める。</li>
                 <li><b>スプライシング</b>：スプライソソームがイントロンを除去しエキソンをつなぐ。</li>
               </ul>
               <p><b>【ステップ2】シャイン・ダルガノ配列とは</b></p>
               <p>これは<b>原核生物</b>のmRNAが持つ配列で、16S rRNA と塩基対を作って
               リボソームを開始コドンに位置づけます。<b>転写後に付加される修飾ではなく、
               もともとDNAにコードされた配列</b>であり、真核生物には存在しません。</p>
               <p>真核生物では代わりに、キャップ結合タンパク質 eIF4E がキャップを認識し、
               小サブユニットが 5' から走査して最初の AUG を見つけます。</p>
               <p>したがって、当てはまらないのは <b>D</b> です。</p>`,
  },

  {
    id: "exp04", ...CAT,
    title: "転写の方向と鋳型鎖", diff: 170, tags: ["鋳型鎖", "コード鎖", "RNA配列"],
    explain: `<p>ある遺伝子の二本鎖DNAの一部を示す。</p>
              <p style="font-family:monospace; background:rgba(255,255,255,.05); padding:10px; border-radius:6px">
                鎖1： 5'-ATG CGA TTC-3'<br>
                鎖2： 3'-TAC GCT AAG-5'
              </p>
              <p><b>鎖2を鋳型</b>として転写したとき、できるmRNAの配列はどれか。</p>`,
    type: "radio",
    options: ["5'-AUG CGA UUC-3'", "5'-UAC GCU AAG-3'", "5'-GAA UCG CAU-3'", "3'-AUG CGA UUC-5'"],
    answer: 0,
    hints: [
      "鋳型鎖と相補的で、かつ逆平行な配列がmRNAになります。",
      "つまり mRNA は「鋳型でないほうの鎖（コード鎖）」と同じ配列になり、T が U に置き換わります。",
    ],
    solution: `<p><b>【ステップ1】鋳型鎖から相補鎖を作る</b></p>
               <p>鋳型は 3'-TAC GCT AAG-5'。これに相補的な塩基を、逆平行に並べます。</p>
               <table>
                 <tr><th>鋳型（3'→5'）</th><td>T</td><td>A</td><td>C</td><td>G</td><td>C</td><td>T</td><td>A</td><td>A</td><td>G</td></tr>
                 <tr><th>mRNA（5'→3'）</th><td>A</td><td>U</td><td>G</td><td>C</td><td>G</td><td>A</td><td>U</td><td>U</td><td>C</td></tr>
               </table>
               <p>よって mRNA は <b>5'-AUG CGA UUC-3'</b>。</p>
               <p><b>【ステップ2】近道</b></p>
               <p>mRNA は<b>コード鎖（鎖1）と同じ配列で、T を U に変えたもの</b>になります。
               鎖1が 5'-ATG CGA TTC-3' なので、答えは即座に 5'-AUG CGA UUC-3' と分かります。
               この関係を覚えておくと計算が要りません。</p>
               <p><b>【他の選択肢】</b></p>
               <ul>
                 <li>B ⇒ 鋳型鎖をそのまま U に置換したものです。</li>
                 <li>C ⇒ 逆向きに読んでいます。</li>
                 <li>D ⇒ 配列は正しいが、方向の表記が逆です。</li>
               </ul>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "exp05", ...CAT,
    title: "選択的スプライシング", diff: 205, tags: ["選択的スプライシング", "プロテオーム", "エキソン"],
    explain: `<p>ヒトのタンパク質コード遺伝子は約2万個しかないのに、作られるタンパク質の種類ははるかに多い。</p>
              <p>この差を生む最大の要因として最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "1つの遺伝子から複数のプロモーターで転写が始まること",
      "選択的スプライシングにより、1つの前駆体mRNAから複数の成熟mRNAができること",
      "遺伝子の重複によりパラログが増えること",
      "リボソームが1本のmRNAを何度も翻訳すること",
    ],
    answer: 1,
    hints: [
      "「遺伝子の数」と「タンパク質の種類」のあいだの段階で、どこが多様性を生んでいるかを考えましょう。",
      "ヒトの多エキソン遺伝子の9割以上が、複数の様式でスプライシングされることが分かっています。",
    ],
    solution: `<p><b>【ステップ1】多様性の生まれる段階を整理する</b></p>
               <ul>
                 <li><b>転写前</b>：遺伝子重複（進化的な時間が必要で、個体内では働かない）</li>
                 <li><b>転写</b>：複数プロモーター（寄与するが限定的）</li>
                 <li><b>転写後</b>：<b>選択的スプライシング</b>、RNA編集</li>
                 <li><b>翻訳後</b>：リン酸化・糖鎖付加などの修飾</li>
               </ul>
               <p><b>【ステップ2】選択的スプライシングの規模</b></p>
               <p>ヒトの多エキソン遺伝子の<b>90%以上</b>が選択的スプライシングを受けます。
               エキソンスキップ、選択的 5'/3' スプライス部位、イントロン保持などの組み合わせで、
               1つの遺伝子から数個〜数十個のアイソフォームが生じます。</p>
               <p>極端な例が、ショウジョウバエの <b>Dscam</b> 遺伝子です。
               4つのエキソンクラスターの組み合わせで理論上 <b>38,016 種類</b>のアイソフォームを作り、
               これは同種のゲノム全遺伝子数を大きく上回ります。</p>
               <p><b>【ステップ3】他の選択肢を退ける</b></p>
               <ul>
                 <li>A ⇒ 寄与はするが、規模は選択的スプライシングに及びません。</li>
                 <li>C ⇒ これは遺伝子数そのものを増やす話で、「遺伝子数より種類が多い」理由になりません。</li>
                 <li>D ⇒ 同じタンパク質の<b>量</b>が増えるだけで、<b>種類</b>は増えません。</li>
               </ul>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "exp06", ...CAT,
    title: "リボソームと翻訳の3部位", diff: 230, tags: ["リボソーム", "A部位", "リボザイム"],
    explain: `<p>翻訳の伸長サイクルにおける tRNA の動きについて、正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "アミノアシルtRNA は P部位に入り、ペプチド転移後に A部位へ移動する。",
      "アミノアシルtRNA は A部位に入り、ペプチド転移後に P部位へ、最後に E部位から出ていく。",
      "ペプチド結合の形成は、リボソーム大サブユニットのタンパク質が触媒する。",
      "転位（トランスロケーション）にGTPは不要である。",
    ],
    answer: 1,
    hints: [
      "A = Aminoacyl（受け入れ）、P = Peptidyl（ペプチジル）、E = Exit（出口）。名前が役割を表しています。",
      "ペプチド転移酵素活性を担っているのは、タンパク質でしょうか、RNAでしょうか。",
    ],
    solution: `<p><b>【ステップ1】3部位の役割と tRNA の流れ</b></p>
               <p>tRNA は <b>A → P → E</b> の順に一方向に流れます。</p>
               <table>
                 <tr><th>部位</th><th>意味</th><th>入っているもの</th></tr>
                 <tr><td>A</td><td>Aminoacyl</td><td>新しく来たアミノアシルtRNA</td></tr>
                 <tr><td>P</td><td>Peptidyl</td><td>伸長中のポリペプチドを担うtRNA</td></tr>
                 <tr><td>E</td><td>Exit</td><td>アミノ酸を手放した空のtRNA</td></tr>
               </table>
               <p><b>【ステップ2】1サイクルの流れ</b></p>
               <ol>
                 <li>コドンに合うアミノアシルtRNAが <b>A部位</b> に入る（GTP加水分解を伴う正確さの検定）</li>
                 <li><b>ペプチド転移</b>：P部位のペプチドが、A部位のアミノ酸のアミノ基に移される</li>
                 <li><b>転位</b>：EF-G/EF-2 が GTP を使ってリボソームを1コドンぶんずらす。
                     A→P、P→E に移り、E部位のtRNAが放出される</li>
               </ol>
               <p><b>【ステップ3】他の選択肢を検討する</b></p>
               <ul>
                 <li>A ⇒ A部位と P部位が逆です。</li>
                 <li>C ⇒ ペプチド結合を触媒するのは<b>23S/28S rRNA</b> です。
                     つまりリボソームは<b>リボザイム</b>であり、この事実は
                     「RNAワールドが先にあった」という仮説の有力な根拠になっています。</li>
                 <li>D ⇒ 転位には <b>GTP</b> が必要です。</li>
               </ul>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "exp07", ...CAT,
    title: "アミノ酸の数から塩基数を求める", diff: 190, tags: ["計算", "コドン", "終止コドン"],
    explain: `<p>あるタンパク質は <b>150 アミノ酸</b> からなる。</p>
              <p>このタンパク質をコードする mRNA のコード領域（開始コドンから終止コドンまで）は、
              最低で何塩基必要か。半角数字で答えなさい。</p>`,
    type: "text",
    answer: ["453"],
    hints: [
      "1アミノ酸につき3塩基が必要です。まず 150 アミノ酸ぶんを計算しましょう。",
      "コード領域には、アミノ酸を指定しない<b>終止コドン</b>の3塩基も含まれます。",
    ],
    solution: `<p><b>【ステップ1】アミノ酸ぶんの塩基数</b></p>
               <p>150 アミノ酸 × 3 塩基 = <b>450 塩基</b></p>
               <p><b>【ステップ2】終止コドンを足す</b></p>
               <p>終止コドン（UAA/UAG/UGA）はアミノ酸を指定しませんが、コード領域には含まれます。<br>
               450 + 3 = <b>453 塩基</b></p>
               <p><b>【ステップ3】よくある間違い</b></p>
               <p>「開始コドンぶんも足すのでは？」と考えたくなりますが、
               開始コドン AUG は<b>メチオニンというアミノ酸を指定している</b>ので、
               150 アミノ酸のなかにすでに数えられています。二重に足さないよう注意しましょう。</p>
               <p class="tiny">なお実際の mRNA には、この 453 塩基に加えて 5'UTR・3'UTR・ポリA尾部が付きます。</p>
               <p>したがって、答えは <b>453</b> です。</p>`,
  },

  {
    id: "exp08", ...CAT,
    title: "変異の種類と影響の大きさ", diff: 250, tags: ["フレームシフト", "NMD", "変異"],
    explain: `<p>あるタンパク質のコード領域の中央付近に生じた変異のうち、
              一般に<b>タンパク質への影響が最も大きい</b>と考えられるものはどれか。</p>`,
    type: "radio",
    options: [
      "コドン3文字目の1塩基置換（同義置換）",
      "保存性の低い部位でのミスセンス変異",
      "1塩基の欠失",
      "3塩基の欠失（インフレーム欠失）",
    ],
    answer: 2,
    hints: [
      "読み枠（リーディングフレーム）がずれるのはどれでしょうか。",
      "3の倍数でない挿入・欠失は、それ以降のアミノ酸配列をすべて変えてしまいます。",
    ],
    solution: `<p><b>【ステップ1】変異の種類と影響の大きさ</b></p>
               <table>
                 <tr><th>変異</th><th>影響</th></tr>
                 <tr><td>同義置換（サイレント）</td><td>アミノ酸が変わらない。影響は小さい</td></tr>
                 <tr><td>ミスセンス</td><td>アミノ酸が1つ変わる。部位の重要性次第</td></tr>
                 <tr><td>インフレーム欠失（3の倍数）</td><td>アミノ酸が1つ減るが、読み枠は保たれる</td></tr>
                 <tr><td><b>フレームシフト（3の倍数でない）</b></td><td><b>以降の配列がすべて変わり、多くは途中で終止コドンが現れる</b></td></tr>
               </table>
               <p><b>【ステップ2】1塩基欠失がなぜ最も重いのか</b></p>
               <p>1塩基が失われると、そこから先のコドンの区切りが1つずれます。
               中央付近で起これば<b>後半のアミノ酸配列がまるごと別物</b>になり、
               ずれた読み枠のどこかで高い確率（およそ3コドンに1回の割合で終止コドンに当たる）で
               <b>未成熟終止コドン</b>が現れ、途中で切れた不完全なタンパク質になります。</p>
               <p>さらに、未成熟終止コドンを持つmRNAは
               <b>ナンセンス依存mRNA分解（NMD）</b>によって積極的に壊されるため、
               タンパク質そのものがほとんど作られなくなることも多いです。</p>
               <p><b>【ステップ3】臨床での対比</b></p>
               <p>デュシェンヌ型筋ジストロフィー（重症）はフレームシフト、
               ベッカー型（軽症）は同じ <i>DMD</i> 遺伝子のインフレーム欠失によることが多く、
               この違いが症状の重さを分けています。</p>
               <p>したがって、正解は <b>1塩基の欠失 (C)</b> です。</p>`,
  },

  {
    id: "exp09", ...CAT,
    title: "遺伝暗号の正確さは誰が決めるか", diff: 290, tags: ["アミノアシルtRNA合成酵素", "実験推論", "アダプター仮説"],
    explain: `<p>古典的な実験がある。システインを結合した Cys-tRNA<sup>Cys</sup> を用意し、
              ラネーニッケルで化学的に処理してシステインをアラニンに変換した。
              こうしてできた <b>Ala-tRNA<sup>Cys</sup></b> を試験管内の翻訳系に加えた。</p>
              <p>合成されるタンパク質はどうなるか。</p>`,
    type: "radio",
    options: [
      "本来システインが入る位置にアラニンが取り込まれる。",
      "本来アラニンが入る位置にアラニンが取り込まれる。",
      "リボソームが誤りを検出し、その位置で翻訳が停止する。",
      "アラニンは取り込まれず、その位置は飛ばされる。",
    ],
    answer: 0,
    hints: [
      "リボソームは、tRNA が「正しいアミノ酸」を運んでいるかを確認しているでしょうか。",
      "リボソームが照合しているのは、mRNAのコドンと tRNA のアンチコドンの対合だけです。",
    ],
    solution: `<p><b>【ステップ1】この実験が問うていること</b></p>
               <p>「アミノ酸を選んでいるのは誰か」という問いです。候補は2つあります。</p>
               <ul>
                 <li>アミノアシルtRNA合成酵素（tRNAにアミノ酸を付ける段階）</li>
                 <li>リボソーム（アミノ酸を取り込む段階）</li>
               </ul>
               <p><b>【ステップ2】結果</b></p>
               <p>実際には、<b>システインが入るはずの位置にアラニンが取り込まれました</b>。
               リボソームは tRNA が何を背負っているかをまったく見ておらず、
               <b>コドンとアンチコドンの対合だけ</b>を照合しているのです。</p>
               <p><b>【ステップ3】結論の意味</b></p>
               <p>したがって、遺伝暗号の正確さは
               <b>アミノアシルtRNA合成酵素がアミノ酸とtRNAを正しく組み合わせる段階</b>で決まっています。
               これが「アダプター仮説」の要で、合成酵素は
               <b>二重の選別（アミノ酸を認識するポケット＋誤って付けたものを削る校正部位）</b>
               によって誤り率を 10<sup>-4</sup>〜10<sup>-5</sup> に抑えています。</p>
               <p><b>【ステップ4】応用</b></p>
               <p>この性質は逆に利用できます。合成酵素とtRNAのペアを人工的に設計すれば、
               天然にはないアミノ酸を狙った位置に入れられる——これが
               <b>非天然アミノ酸の部位特異的導入</b>の原理です。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "exp10", ...CAT,
    title: "原核生物と真核生物の発現機構の違い", diff: 275, tags: ["オペロン", "cDNA", "比較"],
    explain: `<p>大腸菌の遺伝子をそのまま真核細胞に導入しても、正しく発現しないことが多い。</p>
              <p>その理由として<b>適切でない</b>ものはどれか。</p>`,
    type: "radio",
    options: [
      "原核生物のオペロンはポリシストロニックだが、真核生物のリボソームは基本的に1本のmRNAから1種類のタンパク質しか作らない。",
      "原核生物のプロモーター（-10/-35 配列）は、真核生物のRNAポリメラーゼIIには認識されない。",
      "原核生物のmRNAはシャイン・ダルガノ配列で翻訳を開始するが、真核生物はキャップ依存的に開始する。",
      "原核生物の遺伝子にはイントロンが多く含まれ、真核生物のスプライソソームでは処理できない。",
    ],
    answer: 3,
    hints: [
      "原核生物の遺伝子に、そもそもイントロンは多いでしょうか。",
      "「イントロンが多い」のはどちらの生物の特徴だったか思い出しましょう。",
    ],
    solution: `<p><b>【ステップ1】原核生物と真核生物の違いを整理する</b></p>
               <table>
                 <tr><th></th><th>原核生物</th><th>真核生物</th></tr>
                 <tr><td>転写と翻訳</td><td>同じ場所で<b>共役して同時進行</b></td><td>核で転写、細胞質で翻訳（分離）</td></tr>
                 <tr><td>mRNA</td><td><b>ポリシストロニック</b>（オペロン）</td><td>モノシストロニック</td></tr>
                 <tr><td>プロモーター</td><td>-10（TATAAT）／-35 配列、σ因子が認識</td><td>TATAボックス等、基本転写因子が認識</td></tr>
                 <tr><td>翻訳開始</td><td>シャイン・ダルガノ配列</td><td>5'キャップ＋走査</td></tr>
                 <tr><td>イントロン</td><td><b>ほとんど無い</b></td><td>多い</td></tr>
               </table>
               <p><b>【ステップ2】各選択肢を検討する</b></p>
               <ul>
                 <li>A ⇒ 正しい。オペロンの2つ目以降の遺伝子は翻訳されません。</li>
                 <li>B ⇒ 正しい。プロモーターの認識機構がまったく異なります。</li>
                 <li>C ⇒ 正しい。開始点の見つけ方が違います。</li>
                 <li><b>D ⇒ 誤り。</b>イントロンが多いのは<b>真核生物</b>のほうです。
                     原核生物の遺伝子はほぼ連続したコード配列で、イントロンはごく例外的にしかありません。</li>
               </ul>
               <p><b>【ステップ3】実務との接続</b></p>
               <p>この非対称性は逆方向でより深刻です。真核生物の遺伝子を大腸菌で発現させようとすると、
               大腸菌はスプライシングができないため、<b>ゲノムDNAではなく cDNA</b>
               （成熟mRNAから逆転写した、イントロンを含まない配列）を使う必要があります。
               ヒトインスリンの大腸菌生産が cDNA を使うのはこのためです。</p>
               <p>したがって、適切でないのは <b>D</b> です。</p>`,
  },

  {
    id: "exp11", ...CAT,
    title: "mRNA前駆体プロセシングの3過程", diff: 145, tags: ["キャップ", "ポリA", "スプライシング"],
    explain: `<p>真核生物では、転写された mRNA 前駆体が3つの加工を受けて成熟 mRNA になる。</p>
              <p>その3過程の組み合わせとして正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "5'キャップ構造の付加 ／ 3'ポリA尾部の付加 ／ スプライシング",
      "5'キャップ構造の付加 ／ 3'末端のリン酸化 ／ 逆転写",
      "メチル化 ／ アセチル化 ／ ユビキチン化",
      "リボソーム結合 ／ アミノアシル化 ／ ペプチド結合形成",
    ],
    answer: 0,
    hints: [
      "両端の加工が2つ、内部の加工が1つです。",
      "C はヒストン修飾、D は翻訳の話です。",
    ],
    solution: `<p><b>【ステップ1】3つを整理する</b></p>
               <table>
                 <tr><th>過程</th><th>内容</th><th>意義</th></tr>
                 <tr><td><b>5'キャップ</b></td><td>7-メチルグアノシンが<b>5'-5'三リン酸結合</b>で付く（転写のごく初期）</td><td>分解からの保護、核外輸送、翻訳開始因子の足場</td></tr>
                 <tr><td><b>3'ポリA尾部</b></td><td>AAUAAA シグナルの下流で切断され、ポリAポリメラーゼが数百のAを付加</td><td>安定性、核外輸送、翻訳効率</td></tr>
                 <tr><td><b>スプライシング</b></td><td><b>スプライソソーム</b>（snRNP）がイントロンを除去しエキソンを連結</td><td>成熟mRNAの完成、<b>選択的スプライシング</b>による多様化</td></tr>
               </table>
               <p><b>【ステップ2】原核生物との違い</b></p>
               <p>原核生物にはこれらの加工がなく、核もないため<b>転写と翻訳が同時に進行</b>します。
               また原核の mRNA は複数の遺伝子を含む<b>ポリシストロニック</b>、
               真核は基本的に<b>モノシストロニック</b>です。</p>
               <p><b>【ステップ3】不良品の処理</b></p>
               <p>途中に終止コドンが生じた不完全な mRNA は、
               <b>NMD（ナンセンス依存mRNA分解）</b>によって分解されます。
               エキソン連結後に残る目印（EJC）と終止コドンの位置関係で判定される仕組みです。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "exp12", ...CAT,
    title: "遺伝暗号の4つの特徴", diff: 190, tags: ["コドン", "縮重", "遺伝暗号"],
    explain: `<p>遺伝暗号（コドン）の性質として<b>誤っている</b>ものはどれか。</p>`,
    type: "radio",
    options: [
      "1つのコドンが複数のアミノ酸を指定することがある（あいまい性がある）。",
      "1つのアミノ酸を複数のコドンが指定する（縮重している）。",
      "コドンは重複せず、区切り（コンマ）もなく、3塩基ずつ連続して読まれる。",
      "ほぼすべての生物で共通である（普遍性）。",
    ],
    answer: 0,
    hints: [
      "「1対多」の関係がどちら向きに成り立つかを考えてください。",
      "もし1つのコドンが複数のアミノ酸を指定したら、遺伝情報として機能しません。",
    ],
    solution: `<p><b>【ステップ1】方向を間違えない</b></p>
               <p>正しい対応は次のとおりです。</p>
               <ul>
                 <li><b>コドン → アミノ酸</b>：<b>1対1</b>（特異的。あいまいさはない）</li>
                 <li><b>アミノ酸 → コドン</b>：<b>1対多</b>（<b>縮重</b>している）</li>
               </ul>
               <p>A はこの向きを逆にした記述なので誤りです。もし1つのコドンが複数のアミノ酸を
               指定するなら、同じ遺伝子から違うタンパク質ができてしまいます。</p>
               <p><b>【ステップ2】4つの特徴</b></p>
               <table>
                 <tr><th>特徴</th><th>内容</th></tr>
                 <tr><td><b>特異性</b></td><td>1つのコドンは1つのアミノ酸のみを指定</td></tr>
                 <tr><td><b>縮重</b></td><td>64コドンで20アミノ酸 → 複数のコドンが同じアミノ酸を指定</td></tr>
                 <tr><td><b>非重複・コンマなし</b></td><td>3塩基ずつ、重ならず、間に区切りもなく連続して読む</td></tr>
                 <tr><td><b>普遍性</b></td><td>ほぼ全生物共通（ミトコンドリアなどに例外あり）</td></tr>
               </table>
               <p><b>【ステップ3】縮重の意味</b></p>
               <p>縮重の多くは<b>3文字目</b>にあります。そのため3文字目の変異は
               アミノ酸を変えない<b>サイレント変異</b>になりやすく、変異の影響を和らげています。
               この「3文字目のゆるさ」を tRNA 側から説明するのが<b>ゆらぎ仮説</b>です。</p>
               <p>したがって、<b>誤っている</b>のは <b>A</b> です。</p>`,
  },

  {
    id: "exp13", ...CAT,
    title: "アミノアシルtRNA合成酵素の2段階反応", diff: 230, tags: ["アミノアシルtRNA合成酵素", "ATP", "翻訳"],
    explain: `<p>アミノアシルtRNA合成酵素は、アミノ酸を対応する tRNA に結合させる。</p>
              <p>その反応について正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "ATPを使ってアミノ酸をアデニル化し（アミノアシルAMP中間体）、続いてアミノアシル基をtRNAへ転移する。ピロリン酸の分解を伴い、全体として不可逆的に進む。",
      "GTPを使って1段階でアミノ酸をtRNAに結合させ、反応は可逆的である。",
      "リボソーム上で、ペプチジル転移酵素が同時に触媒する。",
      "アミノ酸を認識するだけで、tRNAの種類は区別しない。",
    ],
    answer: 0,
    hints: [
      "「活性化してから渡す」という2段階です。使うヌクレオチドは何でしょうか。",
      "副産物のピロリン酸がどうなるかが、不可逆性の鍵です。",
    ],
    solution: `<p><b>【ステップ1】2段階を書き下す</b></p>
               <ol>
                 <li><b>アデニル化</b>：アミノ酸 + ATP → <b>アミノアシル-AMP</b> + PPi（ピロリン酸）<br>
                 カルボキシル基が AMP と高エネルギー結合を作り、<b>活性化</b>される</li>
                 <li><b>転移</b>：アミノアシル-AMP + tRNA → <b>アミノアシル-tRNA</b> + AMP<br>
                 tRNA の3'末端 CCA のリボースにエステル結合する</li>
               </ol>
               <p><b>【ステップ2】なぜ不可逆か</b></p>
               <p>生じた<b>ピロリン酸(PPi)がピロホスファターゼで2分子のリン酸に加水分解</b>されるためです。
               生成物が消費されることで平衡が右に大きく傾き、実質的に不可逆になります。
               「ATP を AMP + PPi に切る反応は、PPi の分解とセットで不可逆」というのは
               生化学全体に共通する重要な原理です。</p>
               <p><b>【ステップ3】正確さの担保</b></p>
               <p>この酵素は<b>アミノ酸と tRNA の両方</b>を認識します。
               似たアミノ酸を間違えた場合には<b>校正（editing）部位</b>で加水分解して捨てる仕組みがあり、
               翻訳の正確さはここでほぼ決まります。
               リボソームは「渡されたものを繋ぐ」だけなので、
               <b>tRNAに載せる段階の正確さが決定的</b>である点が重要です。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "exp14", ...CAT,
    title: "ゆらぎ仮説", diff: 270, tags: ["ゆらぎ", "アンチコドン", "イノシン"],
    explain: `<p>ヒトの細胞には61種類の tRNA が揃っているわけではないのに、61種類のコドンをすべて読むことができる。</p>
              <p>これを説明する<b>ゆらぎ仮説</b>の内容として正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "コドンの3文字目とアンチコドンの1文字目の塩基対形成にはゆるみがあり、1種類のtRNAが複数のコドンを読める。イノシンは U・C・A のいずれとも対を作れる。",
      "コドンの1文字目とアンチコドンの3文字目にゆるみがあり、複数のアミノ酸が同じコドンから作られる。",
      "リボソームがコドンを2塩基ずつ読むため、3文字目は無視される。",
      "tRNAがアミノ酸を運ばずに、リボソームが直接アミノ酸を選ぶ。",
    ],
    answer: 0,
    hints: [
      "縮重が起きやすいのはコドンの何文字目でしたか。",
      "コドンとアンチコドンは逆向きに対合します。コドン3文字目の相手はアンチコドンの何文字目でしょうか。",
    ],
    solution: `<p><b>【ステップ1】どこがゆらぐのか</b></p>
               <p>コドンとアンチコドンは逆平行に対合するので、
               <b>コドンの5'側1文字目 ↔ アンチコドンの3'側3文字目</b>、
               <b>コドンの3文字目 ↔ アンチコドンの1文字目</b>が向き合います。</p>
               <p>1・2文字目は厳密なワトソン-クリック型対合をしますが、
               <b>コドン3文字目（＝アンチコドン1文字目）</b>だけは非標準的な対合が許されます。
               これが「ゆらぎ（wobble）」です。</p>
               <p><b>【ステップ2】イノシンの威力</b></p>
               <table>
                 <tr><th>アンチコドン1文字目</th><th>読めるコドン3文字目</th></tr>
                 <tr><td>G</td><td>U, C</td></tr>
                 <tr><td>U</td><td>A, G</td></tr>
                 <tr><td><b>I（イノシン）</b></td><td><b>U, C, A</b></td></tr>
                 <tr><td>A, C</td><td>それぞれ U, G のみ</td></tr>
               </table>
               <p>イノシンはアデニンが脱アミノされてできる修飾塩基で、3種類のコドンを読めます。</p>
               <p><b>【ステップ3】遺伝暗号表とのつながり</b></p>
               <p>ゆらぎがあるおかげで、<b>tRNAの種類は61より少なくて済み</b>（ヒトで約48種類）、
               同時に「3文字目が変わってもアミノ酸が変わらない」という
               <b>縮重の構造</b>とも整合します。exp12 の縮重を tRNA 側から説明したもの、と理解してください。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "exp15", ...CAT,
    title: "翻訳後修飾を見分ける", diff: 200, tags: ["翻訳後修飾", "糖鎖", "リン酸化"],
    explain: `<p>次のうち、タンパク質の<b>翻訳後修飾ではない</b>ものはどれか。</p>`,
    type: "radio",
    options: [
      "スプライシング",
      "リン酸化",
      "糖鎖付加（グリコシル化）",
      "ユビキチン化",
    ],
    answer: 0,
    hints: [
      "翻訳後修飾は、できあがったタンパク質に起こる変化です。",
      "1つだけ、RNAに対する操作が混じっています。",
    ],
    solution: `<p><b>【ステップ1】どの分子に起きるか</b></p>
               <p><b>スプライシング</b>は <b>mRNA 前駆体（RNA）</b>に対する加工であり、
               タンパク質の修飾ではありません。「翻訳<b>後</b>」なので、
               対象はタンパク質でなければなりません。</p>
               <p>（なお「タンパク質スプライシング」＝インテインの自己切り出し、という現象も存在しますが、
               試験で問われる文脈では mRNA のスプライシングを指します。）</p>
               <p><b>【ステップ2】代表的な翻訳後修飾</b></p>
               <table>
                 <tr><th>修飾</th><th>内容・意義</th></tr>
                 <tr><td><b>リン酸化</b></td><td>Ser/Thr/Tyr に。活性のオンオフ、シグナル伝達</td></tr>
                 <tr><td><b>糖鎖付加</b></td><td>N型（Asn）/ O型（Ser/Thr）。折りたたみ・品質管理・細胞認識</td></tr>
                 <tr><td><b>ユビキチン化</b></td><td>Lys に。分解や輸送の目印</td></tr>
                 <tr><td><b>アセチル化 / メチル化</b></td><td>Lys など。ヒストンでは遺伝子発現の制御</td></tr>
                 <tr><td><b>限定分解（プロセシング）</b></td><td>プロインスリン → インスリン、シグナルペプチドの切断</td></tr>
                 <tr><td><b>ジスルフィド結合形成</b></td><td>Cys 間。分泌タンパク質の構造安定化</td></tr>
                 <tr><td><b>脂質修飾</b></td><td>ミリストイル化、GPIアンカーなど。膜への係留</td></tr>
                 <tr><td><b>ヒドロキシル化</b></td><td>コラーゲンの Pro/Lys（ビタミンC必要）</td></tr>
               </table>
               <p>「5つ挙げよ」という記述で問われるので、上の表から確実に書ける5つを用意しておきます。</p>
               <p>したがって、<b>翻訳後修飾ではない</b>のは <b>A</b> です。</p>`,
  },

  {
    id: "exp16", ...CAT,
    title: "DNA から tRNA アンチコドンへ", diff: 205, tags: ["配列変換", "アンチコドン", "転写"],
    explain: `<p>ある遺伝子の<b>鋳型鎖（アンチセンス鎖）</b>の一部が <b>3'-TAC-5'</b> であった。</p>
              <p>この部分に対応する tRNA の<b>アンチコドン</b>を、5'→3' の向きで答えなさい（例：5'-AUG-3' なら「AUG」と入力）。</p>`,
    type: "text",
    answer: ["UAC", "uac", "5'-UAC-3'", "5’-UAC-3’"],
    hints: [
      "まず鋳型鎖から mRNA のコドンを作ります。向き（3'と5'）に注意してください。",
      "アンチコドンはコドンと相補的です。結果として鋳型鎖の塩基配列と同じ並びになります（TがUになるだけ）。",
    ],
    solution: `<p><b>【ステップ1】鋳型鎖 → mRNA コドン</b></p>
               <p>鋳型鎖は 3'-T A C-5'。RNAは鋳型に相補的かつ逆平行に作られるので、</p>
               <table>
                 <tr><th>鋳型鎖</th><td>3'-</td><td>T</td><td>A</td><td>C</td><td>-5'</td></tr>
                 <tr><th>mRNA</th><td>5'-</td><td><b>A</b></td><td><b>U</b></td><td><b>G</b></td><td>-3'</td></tr>
               </table>
               <p>コドンは <b>5'-AUG-3'</b>（＝開始コドン、メチオニン）です。</p>
               <p><b>【ステップ2】コドン → アンチコドン</b></p>
               <p>アンチコドンはコドンと相補的・逆平行に対合します。</p>
               <table>
                 <tr><th>コドン</th><td>5'-</td><td>A</td><td>U</td><td>G</td><td>-3'</td></tr>
                 <tr><th>アンチコドン</th><td>3'-</td><td>U</td><td>A</td><td>C</td><td>-5'</td></tr>
               </table>
               <p>これを<b>5'→3' の向きに書き直す</b>と <b>5'-UAC-3'</b> になります。
               答えを書く向きを間違えるのが最頻出のミスです。</p>
               <p><b>【ステップ3】近道</b></p>
               <p>アンチコドンは「鋳型鎖と同じ並び（TをUに置き換えたもの）」になります。
               鋳型鎖 3'-TAC-5' → アンチコドン 3'-UAC-5' = 5'-CAU-3'… と混乱しやすいので、
               <b>必ず向きを書き添えて</b>1段ずつ変換するのが安全です。</p>
               <p><b>答え：UAC</b>（5'-UAC-3'）</p>`,
  },
];
